import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { LogOut, Package, FileText, Users, Home, Truck, Menu, X, Bell, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';
import api from '../utils/api';

export default function Layout({ user, onLogout, children }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  useEffect(() => {
    if (['Admin', 'Supervisor', 'Manager'].includes(user.role)) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user.role]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const navigation = {
    Admin: [
      { name: 'Dashboard', path: '/', icon: Home, module: 'dashboard' },
      { name: 'New Delivery', path: '/new-delivery', icon: Package, module: 'new_delivery' },
      { name: 'Vehicles', path: '/vehicles', icon: Truck, module: 'vehicles' },
      { name: 'Deliveries', path: '/deliveries', icon: FileText, module: 'deliveries' },
      { name: 'Pallet Statement', path: '/pallet-statement', icon: FileText, module: 'pallet_statement' },
      { name: 'Notifications', path: '/notifications', icon: Bell, badge: unreadCount, module: 'notifications' },
      { name: 'RouteOps', path: '/routeops', icon: Sparkles, module: 'routeops' },
      { name: 'User Management', path: '/user-management', icon: Users, module: 'user_management' },
      { name: 'Backups', path: '/backups', icon: FileText, module: 'backups' },
    ],
    Manager: [
      { name: 'Dashboard', path: '/', icon: Home, module: 'dashboard' },
      { name: 'New Delivery', path: '/new-delivery', icon: Package, module: 'new_delivery' },
      { name: 'Vehicles', path: '/vehicles', icon: Truck, module: 'vehicles' },
      { name: 'Deliveries', path: '/deliveries', icon: FileText, module: 'deliveries' },
      { name: 'Pallet Statement', path: '/pallet-statement', icon: FileText, module: 'pallet_statement' },
      { name: 'Notifications', path: '/notifications', icon: Bell, badge: unreadCount, module: 'notifications' },
      { name: 'RouteOps', path: '/routeops', icon: Sparkles, module: 'routeops' },
    ],
    Supervisor: [
      { name: 'New Delivery', path: '/', icon: Package, module: 'new_delivery' },
      { name: 'Deliveries', path: '/deliveries', icon: FileText, module: 'deliveries' },
      { name: 'Pallet Monitoring', path: '/pallet-monitoring', icon: Package, module: 'pallet_monitoring' },
      { name: 'Notifications', path: '/notifications', icon: Bell, badge: unreadCount, module: 'notifications' },
    ],
    Driver: [
      { name: 'My Deliveries', path: '/', icon: Package, module: 'deliveries' },
      { name: 'Pallet Monitoring', path: '/pallet-monitoring', icon: Package, module: 'pallet_monitoring' },
    ],
  };

  // Role-default module list (must mirror backend ROLE_DEFAULT_MODULES)
  const ROLE_DEFAULT_MODULES = {
    Admin: ['dashboard', 'new_delivery', 'vehicles', 'deliveries', 'pallet_statement', 'pallet_monitoring', 'notifications', 'routeops', 'user_management', 'backups'],
    Manager: ['dashboard', 'new_delivery', 'vehicles', 'deliveries', 'pallet_statement', 'notifications', 'routeops'],
    Supervisor: ['new_delivery', 'deliveries', 'pallet_monitoring', 'notifications'],
    Driver: ['deliveries', 'pallet_monitoring'],
  };

  const allowedModules = Array.isArray(user.allowed_modules)
    ? user.allowed_modules
    : (ROLE_DEFAULT_MODULES[user.role] || []);

  const links = (navigation[user.role] || []).filter(
    link => !link.module || allowedModules.includes(link.module)
  );

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-void)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ background: 'var(--bg-base)', borderColor: 'var(--border-color)' }}>
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="flex justify-between items-center h-16">
            {/* Logo Section */}
            <div className="flex items-center gap-3 min-w-[180px]">
              <img src="/workforce-logo.png" alt="WorkForce" className="h-[60px] w-auto" />
              <div className="hidden sm:block">
                <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: 'var(--amber-glow)', color: 'var(--amber)', fontFamily: 'var(--fm)' }}>
                  {user.role}
                </span>
                {user.is_test_user && (
                  <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded" style={{ background: 'var(--red-dim)', color: 'var(--red)', fontFamily: 'var(--fm)' }}>
                    SANDBOX
                  </span>
                )}
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1 flex-1 justify-center mx-4">
              {links.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
                    style={{
                      fontFamily: 'var(--fd)',
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      fontSize: '0.8rem',
                      background: isActive ? 'var(--amber-glow)' : 'transparent',
                      color: isActive ? 'var(--amber)' : 'var(--text-secondary)',
                      borderLeft: isActive ? '2px solid var(--amber)' : '2px solid transparent',
                    }}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span>{link.name}</span>
                    {link.badge > 0 && (
                      <span className="absolute -top-1 -right-1 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg" style={{ background: 'var(--red)', color: '#fff', fontFamily: 'var(--fm)', fontSize: '0.65rem' }}>
                        {link.badge > 9 ? '9+' : link.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* User Actions - Desktop */}
            <div className="hidden md:flex items-center gap-3 min-w-[200px] justify-end">
              <span className="text-sm hidden lg:block font-medium" style={{ color: 'var(--text-secondary)' }}>{user.full_name}</span>
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2a3f5a, #1a2f4a)', border: '2px solid var(--border-bright)' }}>
                <span className="font-bold text-xs" style={{ color: 'var(--amber)', fontFamily: 'var(--fm)' }}>
                  {getInitials(user.full_name)}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                data-testid="logout-button"
                className="text-sm hover:opacity-80 transition-opacity"
                style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)', letterSpacing: '0.05em', textTransform: 'uppercase' }}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2a3f5a, #1a2f4a)' }}>
                <span className="font-bold text-xs" style={{ color: 'var(--amber)', fontFamily: 'var(--fm)' }}>
                  {getInitials(user.full_name)}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
            <div className="px-4 py-3 space-y-1">
              <div className="pb-2 mb-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.full_name}</p>
                <span className="text-xs font-semibold" style={{ color: 'var(--amber)' }}>{user.role}</span>
              </div>
              {links.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="relative flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium transition-all"
                    style={{
                      background: isActive ? 'var(--amber-glow)' : 'transparent',
                      color: isActive ? 'var(--amber)' : 'var(--text-secondary)',
                    }}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="flex-1">{link.name}</span>
                    {link.badge > 0 && (
                      <span className="text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" style={{ background: 'var(--red)', color: '#fff' }}>
                        {link.badge > 9 ? '9+' : link.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
              <div className="pt-2 mt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setMobileMenuOpen(false); onLogout(); }}
                  className="w-full justify-start px-3 py-3"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <LogOut className="h-5 w-5 mr-3" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-12 xl:px-16 py-4 sm:py-6 lg:py-8">
        {children}
      </main>
    </div>
  );
}
